import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ChevronRight, ChevronDown, Layers, Package } from 'lucide-react';
import api from '../services/api';

// Muted Luxury Environments for Valley Collection
const ENVIRONMENTS = {
  leatherStudio: 'https://static.prod-images.emergentagent.com/jobs/2c0beaa6-c4c8-45fb-8720-fb0ecbfcdc2f/images/047570e6fe477a8845cd6d17ba8cf468fd2aa250d428ea4edd198ee43756b351.png',
  showroom: 'https://static.prod-images.emergentagent.com/jobs/2c0beaa6-c4c8-45fb-8720-fb0ecbfcdc2f/images/fd0c0254d081c0ad82efb171f2148a13c3f57baab1ab3831e270e66ca76b10d8.png',
  lifestyleSpace: 'https://static.prod-images.emergentagent.com/jobs/2c0beaa6-c4c8-45fb-8720-fb0ecbfcdc2f/images/2e8dcf7688a45cfa12e6ef598d51546194491c707949b3f70192cf3e5463c6fd.png'
};

// Product categories for Valley Collection - Updated handles
const CATEGORIES = {
  fullSets: [
    { handle: 'valley-track-suit-red-black', name: 'Valley Track Suit', variant: 'Red/Black', price: 1000 },
    { handle: 'valley-track-suit-black-white', name: 'Valley Track Suit', variant: 'Black/White', price: 1000 },
    { handle: 'valley-track-suit-red-black-blue', name: 'Valley Track Suit', variant: 'Red/Black/Blue', price: 1000 }
  ],
  bombers: [
    { handle: 'valley-bomber-red-white-blue', name: 'The Valley Bomber', variant: 'Red/White/Blue', price: 544, tag: 'Leather' },
    { handle: 'valley-bomber-red-black', name: 'The Valley Bomber', variant: 'Red/Black', price: 544 },
    { handle: 'the-valley-bomber-black-white', name: 'The Valley Bomber', variant: 'Black/White', price: 544, tag: 'Leather' },
    { handle: 'valley-bomber-black-white', name: 'Valley Bomber', variant: 'Black/White', price: 544 },
    { handle: 'chambray-valley-bomber', name: 'Chambray Valley Bomber', variant: 'Chambray', price: 288 },
    { handle: 'kids-leather-valley-bomber', name: 'Kids Valley Bomber', variant: 'Kids', price: 388 }
  ],
  pants: [
    { handle: 'valleyleathertrackpants', name: 'Leather Valley Track Pants', variant: 'Black/White', price: 544 },
    { handle: 'valley-track-pants-black-white', name: 'Valley Track Pants', variant: 'Black/White', price: 544 },
    { handle: 'chi-valley-track-pants', name: 'Chi Valley Track Pants', variant: 'Chicago', price: 544 },
    { handle: 'elevated-valley-sweats', name: 'Elevated Valley Sweats', variant: 'Black', price: 110 },
    { handle: 'black-valley-denim-w-hand-painted-valley-1', name: 'Black Valley Denim', variant: 'Hand Painted', price: 198 }
  ],
  footwear: [
    { handle: 'the-valley-high-top-sneaker', name: 'The Valley High', variant: 'Black/White', price: 188 }
  ],
  luggage: [
    { handle: 'the-valley-duffle', name: 'The Valley Duffle', variant: 'Red/Black/Blue', price: 200 },
    { handle: 'the-mini-valley-duffle', name: 'Mini Valley Duffle', variant: 'Classic', price: 185 },
    { handle: 'the-valley-duffle-via-santa-monica-1', name: 'The Valley Duffle', variant: 'Santa Monica', price: 320 },
    { handle: 'santa-monica-mini-valley-duffel-bag', name: 'Mini Valley Duffle', variant: 'Santa Monica', price: 185 },
    { handle: 'the-valley-personal-travel-set-via-santa-monica', name: 'Valley Travel Set', variant: 'Santa Monica', price: 55 }
  ]
};

const ValleyCollectionExperience = () => {
  const navigate = useNavigate();
  const containerRef = useRef(null);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('sets'); // 'sets' or 'separates'
  const [activeCategory, setActiveCategory] = useState('bombers');

  // Fetch products from Shopify
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await api.get('/shopify/products');
        if (response.data?.products) {
          setProducts(response.data.products);
        }
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  // Scroll tracking
  useEffect(() => {
    const handleScroll = () => {
      if (!containerRef.current) return;
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = Math.min(scrollTop / docHeight, 1);
      setScrollProgress(progress);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const getProductByHandle = (handle) => {
    // Try exact match first
    let product = products.find(p => p.handle === handle);
    if (product) return product;
    
    // Try partial match - handle contains or is contained
    product = products.find(p => {
      const pHandle = p.handle?.toLowerCase() || '';
      const searchHandle = handle?.toLowerCase() || '';
      return pHandle.includes(searchHandle) || searchHandle.includes(pHandle);
    });
    if (product) return product;
    
    // Try matching by splitting on dashes and finding overlap
    const searchWords = handle?.toLowerCase().split('-').filter(w => w.length > 2) || [];
    product = products.find(p => {
      const pWords = p.handle?.toLowerCase().split('-').filter(w => w.length > 2) || [];
      const matchCount = searchWords.filter(sw => pWords.some(pw => pw.includes(sw) || sw.includes(pw))).length;
      return matchCount >= 2;
    });
    
    return product;
  };

  const getProductImage = (handle) => {
    const product = getProductByHandle(handle);
    if (product?.images?.[0]) {
      return product.images[0].src || product.images[0].url;
    }
    return '';
  };

  const handleShopProduct = (handle) => {
    // Navigate to Shopify product page
    window.open(`https://prsvr.store/products/${handle}`, '_blank');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-2 border-stone-600 border-t-stone-400 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-stone-500 uppercase tracking-[0.3em] text-xs">Loading Collection</p>
        </div>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="bg-[#0a0a0a] text-stone-200 overflow-x-hidden">
      {/* Minimal Progress Bar */}
      <div className="fixed top-0 left-0 right-0 z-50 h-px bg-stone-900">
        <div 
          className="h-full bg-stone-500 transition-all duration-100"
          style={{ width: `${scrollProgress * 100}%` }}
        />
      </div>

      {/* Section 1: Hero */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Parallax Background */}
        <div 
          className="absolute inset-0 transition-transform duration-500"
          style={{ transform: `scale(${1 + scrollProgress * 0.08}) translateY(${scrollProgress * -30}px)` }}
        >
          <img 
            src={ENVIRONMENTS.leatherStudio}
            alt="Valley Collection"
            className="w-full h-full object-cover opacity-50"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a] via-transparent to-[#0a0a0a]"></div>
        </div>

        {/* Hero Content */}
        <div className="relative z-10 text-center px-6 max-w-3xl mx-auto">
          <p className="text-stone-500 uppercase tracking-[0.4em] text-xs mb-6">
            Premium Leather & Lifestyle
          </p>
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-light uppercase tracking-[0.1em] mb-8 text-stone-100">
            The Valley Collection
          </h1>
          <p className="text-lg text-stone-400 mb-12 font-light tracking-wide">
            Where craftsmanship meets contemporary luxury.
          </p>
          
          <ChevronDown className="w-5 h-5 text-stone-600 mx-auto animate-bounce" />
        </div>
      </section>

      {/* Section 2: Full Sets - Hero Feature */}
      <section className="relative min-h-screen py-32">
        {/* Environment Background */}
        <div 
          className="absolute inset-0 transition-all duration-700"
          style={{ opacity: Math.max(0.25, 0.6 - scrollProgress) }}
        >
          <img 
            src={ENVIRONMENTS.showroom}
            alt="Showroom"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-[#0a0a0a]/70"></div>
        </div>

        <div className="relative z-10 container mx-auto px-6">
          <div className="text-center mb-16">
            <p className="text-stone-600 uppercase tracking-[0.3em] text-xs mb-4">The Complete Statement</p>
            <h2 className="text-3xl md:text-5xl font-light uppercase tracking-wide text-stone-100 mb-4">
              Valley Track Suits
            </h2>
            <p className="text-stone-500 font-light max-w-xl mx-auto">
              Full leather bomber and track pant sets. Made to order, crafted to perfection.
            </p>
          </div>

          {/* Full Sets Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto mb-16">
            {CATEGORIES.fullSets.slice(0, 3).map((item, index) => (
              <div 
                key={item.handle}
                className="group relative bg-[#0d0d0d] border border-stone-900 overflow-hidden hover:border-stone-700 transition-all duration-500"
                style={{
                  transform: `translateY(${Math.max(0, (0.3 - scrollProgress) * (40 + index * 10))}px)`,
                  opacity: Math.min(1, (scrollProgress - 0.1) * 4)
                }}
              >
                <div className="relative p-8">
                  <div className="aspect-square mb-6 flex items-center justify-center overflow-hidden bg-stone-950/50">
                    {getProductImage(item.handle) ? (
                      <img 
                        src={getProductImage(item.handle)}
                        alt={item.name}
                        className="max-h-64 object-contain group-hover:scale-105 transition-transform duration-700"
                        style={{ filter: 'drop-shadow(0 20px 40px rgba(0,0,0,0.5)) brightness(0.95)' }}
                      />
                    ) : (
                      <div className="text-stone-700 text-center">
                        <Package className="w-12 h-12 mx-auto mb-2" />
                        <p className="text-xs uppercase tracking-wider">{item.variant}</p>
                      </div>
                    )}
                  </div>
                  
                  <div className="space-y-2">
                    <p className="text-stone-600 text-xs uppercase tracking-wider">{item.variant}</p>
                    <h3 className="text-lg font-light uppercase tracking-wide text-stone-200">{item.name}</h3>
                    <p className="text-stone-500 text-xs">Bomber + Track Pants</p>
                  </div>
                  
                  <div className="flex items-center justify-between mt-6">
                    <span className="text-xl font-light text-stone-300">${item.price}</span>
                    <Button 
                      onClick={() => handleShopProduct(item.handle)}
                      variant="outline"
                      className="border-stone-700 text-stone-400 hover:bg-stone-800 hover:text-stone-200 rounded-none text-xs uppercase tracking-wider"
                      data-testid={`shop-${item.handle}-btn`}
                    >
                      Shop Set
                    </Button>
                  </div>
                </div>

                {/* Made to Order Badge */}
                <div className="absolute top-4 right-4">
                  <span className="px-2 py-1 bg-stone-900/80 border border-stone-800 text-[10px] text-stone-500 uppercase tracking-wider">
                    Made to Order
                  </span>
                </div>
              </div>
            ))}
          </div>

          {/* View More Sets CTA */}
          <div className="text-center">
            <Button 
              onClick={() => window.open('https://prsvr.store/collections/the-valley-collection', '_blank')}
              className="bg-stone-100 hover:bg-white text-stone-900 font-medium text-sm px-8 py-5 uppercase tracking-[0.2em] rounded-none"
            >
              View All Sets
              <ChevronRight className="w-4 h-4 ml-3" />
            </Button>
          </div>
        </div>
      </section>

      {/* Section 3: Separates Toggle */}
      <section className="relative py-32 bg-gradient-to-b from-[#0a0a0a] via-[#0d0d0d] to-[#0a0a0a]">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <p className="text-stone-600 uppercase tracking-[0.3em] text-xs mb-4">Or Build Your Way</p>
            <h2 className="text-3xl md:text-4xl font-light uppercase tracking-wide text-stone-200 mb-8">
              Shop Separates
            </h2>
            
            {/* Category Toggle */}
            <div className="flex flex-wrap justify-center gap-2 mb-12">
              {[
                { key: 'bombers', label: 'Bombers' },
                { key: 'pants', label: 'Pants' },
                { key: 'footwear', label: 'Footwear' },
                { key: 'luggage', label: 'Luggage' }
              ].map((cat) => (
                <button
                  key={cat.key}
                  onClick={() => setActiveCategory(cat.key)}
                  className={`px-6 py-3 text-xs uppercase tracking-wider transition-all duration-300 ${
                    activeCategory === cat.key 
                      ? 'bg-stone-100 text-stone-900' 
                      : 'bg-stone-900/50 text-stone-500 hover:bg-stone-800 hover:text-stone-300 border border-stone-800'
                  }`}
                >
                  {cat.label}
                </button>
              ))}
            </div>
          </div>

          {/* Separates Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {CATEGORIES[activeCategory]?.slice(0, 4).map((item, index) => (
              <div 
                key={item.handle}
                className="group relative bg-[#0d0d0d] border border-stone-900 overflow-hidden hover:border-stone-700 transition-all duration-500"
              >
                {/* Subtle Environment Background */}
                <div className="absolute inset-0 opacity-5">
                  <img src={ENVIRONMENTS.lifestyleSpace} alt="" className="w-full h-full object-cover" />
                </div>
                
                <div className="relative p-6">
                  <div className="aspect-square mb-4 flex items-center justify-center overflow-hidden bg-stone-950/30">
                    {getProductImage(item.handle) ? (
                      <img 
                        src={getProductImage(item.handle)}
                        alt={item.name}
                        className="max-h-48 object-contain group-hover:scale-105 transition-transform duration-700"
                        style={{ filter: 'drop-shadow(0 15px 30px rgba(0,0,0,0.4)) brightness(0.95)' }}
                      />
                    ) : (
                      <div className="text-stone-700 text-center">
                        <Layers className="w-10 h-10 mx-auto mb-2" />
                        <p className="text-xs">{item.variant}</p>
                      </div>
                    )}
                  </div>
                  
                  {item.tag && (
                    <span className="inline-block px-2 py-1 bg-stone-900/80 border border-stone-800 text-[9px] text-stone-500 uppercase tracking-wider mb-2">
                      {item.tag}
                    </span>
                  )}
                  
                  <h3 className="text-sm font-light uppercase tracking-wide text-stone-200 mb-1">{item.name}</h3>
                  <p className="text-stone-600 text-xs mb-4">{item.variant}</p>
                  
                  <div className="flex items-center justify-between">
                    <span className="text-lg font-light text-stone-300">${item.price}</span>
                    <Button 
                      onClick={() => handleShopProduct(item.handle)}
                      size="sm"
                      variant="outline"
                      className="border-stone-700 text-stone-400 hover:bg-stone-800 hover:text-stone-200 rounded-none text-[10px] uppercase tracking-wider px-3"
                      data-testid={`shop-${item.handle}-btn`}
                    >
                      Shop
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* View All Category CTA */}
          <div className="text-center mt-12">
            <Button 
              onClick={() => window.open('https://prsvr.store/collections/the-valley-collection', '_blank')}
              variant="outline"
              className="border-stone-700 text-stone-400 hover:bg-stone-800 hover:text-stone-200 rounded-none text-sm uppercase tracking-wider px-8 py-5"
            >
              View All {activeCategory.charAt(0).toUpperCase() + activeCategory.slice(1)}
              <ChevronRight className="w-4 h-4 ml-3" />
            </Button>
          </div>
        </div>
      </section>

      {/* Section 4: Signature Piece - Valley Bomber Feature */}
      <section className="relative min-h-screen py-32 overflow-hidden">
        {/* Environment Background */}
        <div 
          className="absolute inset-0"
          style={{ opacity: Math.min(0.4, (scrollProgress - 0.4) * 1.2) }}
        >
          <img 
            src={ENVIRONMENTS.lifestyleSpace}
            alt="Lifestyle Space"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a0a] via-[#0a0a0a]/80 to-transparent"></div>
        </div>

        <div className="relative z-10 container mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center min-h-[70vh]">
            {/* Featured Bomber Info */}
            <div className="space-y-8">
              <div>
                <p className="text-stone-600 uppercase tracking-[0.3em] text-xs mb-3">The Signature Piece</p>
                <h2 className="text-3xl md:text-5xl font-light uppercase tracking-wide text-stone-100">
                  The Valley Bomber
                </h2>
              </div>
              
              <p className="text-lg text-stone-400 leading-relaxed font-light">
                The Co-Founder's personal favorite. A contemporary take on the classic bomber jacket, 
                crafted with premium leather and timeless design sensibility.
              </p>

              <div className="space-y-4">
                <p className="text-stone-500 text-sm">Available in:</p>
                <div className="flex flex-wrap gap-2">
                  {['Leather', 'Suede', 'Denim', 'Chambray'].map((material) => (
                    <span key={material} className="px-4 py-2 bg-stone-900/50 border border-stone-800 rounded text-xs text-stone-500 uppercase tracking-wider">
                      {material}
                    </span>
                  ))}
                </div>
              </div>

              <div className="flex items-baseline gap-4">
                <span className="text-2xl font-light text-stone-200">From $288</span>
                <span className="text-stone-600 text-sm">to $644</span>
              </div>

              <Button 
                onClick={() => setActiveCategory('bombers')}
                className="bg-stone-100 hover:bg-white text-stone-900 font-medium text-sm px-8 py-6 uppercase tracking-[0.2em] rounded-none"
                data-testid="shop-bombers-btn"
              >
                Explore Bombers
                <ChevronRight className="w-4 h-4 ml-3" />
              </Button>
            </div>

            {/* Featured Bomber Image */}
            <div 
              style={{
                transform: `translateX(${Math.max(0, (0.5 - scrollProgress) * 40)}px)`,
                opacity: Math.min(1, (scrollProgress - 0.4) * 3)
              }}
            >
              <img 
                src={getProductImage('the-valley-bomber') || getProductImage('the-valley-bomber-black-white')}
                alt="The Valley Bomber"
                className="w-full max-w-md mx-auto"
                style={{ filter: 'drop-shadow(0 30px 60px rgba(0,0,0,0.7)) brightness(0.95)' }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Section 5: Final CTA */}
      <section className="relative min-h-[60vh] flex items-center justify-center py-32">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a] via-[#0d0d0d] to-[#0a0a0a]"></div>
        
        <div className="relative z-10 text-center px-6 max-w-2xl mx-auto">
          <p className="text-stone-600 uppercase tracking-[0.4em] text-xs mb-8">The Valley Collection</p>
          <h2 className="text-3xl md:text-4xl font-light uppercase tracking-wide mb-8 text-stone-200">
            Crafted for Those Who Lead
          </h2>
          <p className="text-stone-500 mb-12 font-light">
            Premium leather goods and lifestyle essentials. Made to order, built to last.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              onClick={() => window.open('https://prsvr.store/collections/the-valley-collection', '_blank')}
              className="bg-stone-100 hover:bg-white text-stone-900 font-medium text-sm px-10 py-6 uppercase tracking-[0.2em] rounded-none"
              data-testid="shop-full-collection-btn"
            >
              Shop Full Collection
              <ChevronRight className="w-4 h-4 ml-3" />
            </Button>
            <Button 
              onClick={() => navigate('/')}
              variant="outline"
              className="border-stone-800 text-stone-500 hover:bg-stone-900 font-medium text-sm px-10 py-6 uppercase tracking-[0.2em] rounded-none"
            >
              Back to Store
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ValleyCollectionExperience;
